import { useEffect, useState } from 'react';
import InitialWelcomeScreen from './InitialWelcomeScreen';
import BirthdayAnimation from './BirthdayAnimation';
import ForcePostDialog from './ForcePostDialog';
import { useLocation } from 'react-router-dom';
import Api from '../../ajax';
import './WelcomeScreen.css';
import ClasspepLoader from '../common/ClasspepLoader';

const WelcomeScreenDialog = ({ isLoggedIn, currentUser, onWelcomeComplete }) => {
  const [step, setStep] = useState('idle');
  const [birthdayData, setBirthdayData] = useState(null);
  const [forcePosts, setForcePosts] = useState([]);
  const [show, setShow] = useState(false);
  const [forcePostsLoaded, setForcePostsLoaded] = useState(false);
  const [isLoadingForcePosts, setIsLoadingForcePosts] = useState(false);
  const [showintro, setShowintro] = useState(false);
  const [showBirthday, setShowBirthday] = useState(false);
  const [showpost, setShowpost] = useState(false);
  const [flagsLoaded, setFlagsLoaded] = useState(false);
  const api = new Api();
  const location = useLocation();
  const mentyp = new URLSearchParams(location.search).get('mentyp');

  useEffect(() => {
    if (isLoggedIn && currentUser) {
      startSequence();
    } else {
      console.error('WelcomeScreenDialog: Not ready - isLoggedIn:', isLoggedIn, 'currentUser:', !!currentUser);
    }
  }, [isLoggedIn, currentUser, flagsLoaded]); // Add flagsLoaded to dependency

  useEffect(() => {
    if (isLoggedIn && currentUser) {
      const setkeys = ['SCHOLAR_WELCOMESCREEN_INTRO', 'SCHOLAR_WELCOMESCREEN_BIRTHDAY', 'POSTS_ENFORCE_POST'];

      api.post(
        '/menu-action-access/get-by-settings',
        { keys: setkeys },
        (res) => {
          if (res && res.s) {
            if (res.d && res.d.list && Array.isArray(res.d.list)) {
              setShowintro(res.d.list.includes('SCHOLAR_WELCOMESCREEN_INTRO'));
              setShowBirthday(res.d.list.includes('SCHOLAR_WELCOMESCREEN_BIRTHDAY'));
              setShowpost(res.d.list.includes('POSTS_ENFORCE_POST'));
            } else {
              setShowintro(false);
              setShowBirthday(false);
              setShowpost(false);
            }
            setFlagsLoaded(true); // Mark flags as loaded
          } else {
            console.error('Flags API returned error');
            setShowintro(false);
            setShowBirthday(false);
            setShowpost(false);
            setFlagsLoaded(true);
          }
        },
        (error) => {
          console.error('Flags API call error:', error);
          setShowintro(false);
          setShowBirthday(false);
          setShowpost(false);
          setFlagsLoaded(true);
        }
      );
    } else {
      console.error('WelcomeScreenDialog: Not ready for flags - isLoggedIn:', isLoggedIn, 'currentUser:', !!currentUser);
    }
  }, [isLoggedIn, currentUser]);

  const checkAllItemsCompleted = async () => {
    const today = new Date().toISOString().split('T')[0];
    const lastInitialShown = localStorage.getItem('last_initial_welcome_date');

    const pendingItems = [];

    // Only add initial if showintro is true
    if (showintro) {
      if (lastInitialShown !== today && step === 'idle') {
        pendingItems.push('initial');
      }
    }

    // Only add birthday if showBirthday is true
    if (showBirthday) {
      const bday = checkBirthday();

      if (bday && step !== 'birthday' && !birthdayData) {
        pendingItems.push('birthday');
      }
    }

    // Only add forceposts if showpost is true
    if (showpost) {
      // Only check force posts if not already loaded and not currently loading
      if (step !== 'forceposts' && !forcePostsLoaded && !isLoadingForcePosts) {
        const forced = await loadForcePosts();

        if (forced.length > 0) {
          pendingItems.push('forceposts');
        }
      } else if (forcePostsLoaded && forcePosts.length > 0 && step !== 'forceposts') {
        pendingItems.push('forceposts');
      }
    }

    return pendingItems;
  };

  const startSequence = async () => {

    // Wait for flags to load if they haven't yet
    if (!flagsLoaded) {
      return;
    }

    if (!showintro && !showBirthday && !showpost) {
      finishSequence();

      return;
    }

    const pendingItems = await checkAllItemsCompleted();

    if (pendingItems.length === 0) {
      finishSequence();

      return;
    }

    const firstPending = pendingItems[0];

    if (firstPending === 'birthday' && !birthdayData) {
      const bday = checkBirthday();

      if (bday) setBirthdayData(bday);
    }
    setShow(true);
    setStep(firstPending);
  };

  const handleInitialContinue = async () => {
    const today = new Date().toISOString().split('T')[0];

    localStorage.setItem('last_initial_welcome_date', today);
    await proceedToNextStep('initial');
  };

  const handleInitialCancel = async () => {
    const today = new Date().toISOString().split('T')[0];

    localStorage.setItem('last_initial_welcome_date', today);
    await proceedToNextStep('initial');
  };

  const proceedToNextStep = async (currentStep) => {
    const pendingItems = await checkAllItemsCompleted();

    // Remove the current step from pending items
    const remainingItems = pendingItems.filter(item => item !== currentStep);

    if (remainingItems.length === 0) {
      finishSequence();

      return;
    }

    const nextStep = remainingItems[0];

    if (nextStep === 'birthday' && !birthdayData) {
      const bday = checkBirthday();

      if (bday) setBirthdayData(bday);
    }
    setStep(nextStep);
    setShow(true);
  };

  const handleBirthdayComplete = async () => {
    await proceedToNextStep('birthday');
  };

  const finishSequence = async () => {
    const today = new Date().toISOString().split('T')[0];

    localStorage.setItem('last_welcome_sequence_date', today);

    setStep('idle');
    setShow(false);
    setBirthdayData(null);
    setForcePosts([]);
    setForcePostsLoaded(false);

    onWelcomeComplete?.({
      hadBirthday: !!birthdayData,
      hadForcePosts: forcePosts.length > 0,
    });
  };

  const checkBirthday = () => {
    if (!currentUser?.dob) return null;

    const today = new Date();
    const [d, m] = [
      today.getDate().toString().padStart(2, '0'),
      (today.getMonth() + 1).toString().padStart(2, '0'),
    ];

    const [dobDay, dobMonth] = currentUser.dob.split('-');

    if (dobDay === d && dobMonth === m) {
      return {
        is_birthday: true,
        user_name: currentUser.user.name || 'Student',
        birth_date: currentUser.dob,
      };
    }

    return null;
  };

  const loadForcePosts = async () => {
    // Return cached results if already loaded
    if (forcePostsLoaded) {
      return forcePosts;
    }

    // Prevent concurrent calls
    if (isLoadingForcePosts) {
      while (isLoadingForcePosts) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      return forcePosts;
    }

    setIsLoadingForcePosts(true);

    return new Promise((resolve) => {
      const requestData = {
        index: 0,
        search: '',
        filter: [],
        mentyp: mentyp || null,
        limit: 100,
        force_post: true,
      };

      api.post('/posts/list', requestData, (res) => {
        try {
          if (res?.s && res.d?.list && Array.isArray(res.d.list)) {
            setForcePosts(res.d.list);
            setForcePostsLoaded(true);
            setIsLoadingForcePosts(false);
            resolve(res.d.list);
          } else {
            setForcePosts([]);
            setForcePostsLoaded(true);
            setIsLoadingForcePosts(false);
            resolve([]);
          }
        } catch (error) {
          console.error('Force posts API response parsing error:', error);
          setForcePosts([]);
          setForcePostsLoaded(true);
          setIsLoadingForcePosts(false);
          resolve([]);
        }
      }, (error) => {
        console.error('Force posts API error:', error);
        setForcePosts([]);
        setForcePostsLoaded(true);
        setIsLoadingForcePosts(false);
        resolve([]);
      });
    });
  };

  const markPostRead = async (receiverId) => {
    return new Promise((resolve) => {
      api.post('/posts/read-recipet', {
        post_receiver_id: receiverId,
      }, (res) => {
        resolve(res?.s || false);
      }, (error) => {
        console.error('Mark post read API error:', error);
        resolve(false);
      });
    });
  };

  // Show loader until flagsLoaded is true (get-by-settings API complete)
  if (!flagsLoaded) return <ClasspepLoader />;
  if (!show) return <ClasspepLoader />;

  return (
    <>
      {showintro && (
        <InitialWelcomeScreen
          open={step === 'initial'}
          onContinue={handleInitialContinue}
          onCancel={handleInitialCancel}
        />
      )}

      {showBirthday && (
        <BirthdayAnimation
          open={step === 'birthday'}
          birthdayData={birthdayData}
          onAnimationComplete={handleBirthdayComplete}
        />
      )}

      {showpost && (
        <ForcePostDialog
          open={step === 'forceposts'}
          forcePostsData={forcePosts}
          onMarkPostRead={markPostRead}
          onClose={() => finishSequence()}
        />
      )}
    </>
  );
};

export default WelcomeScreenDialog;